import type { FeatureModule, FeatureState } from '../types';

export default function createHideImagesModule(): FeatureModule {
  let enabled = false;
  const STYLE_ID = 'accessify-hide-images';
  let bgImageOriginals = new Map<HTMLElement, string>();
  let mutationObserver: MutationObserver | null = null;

  const CSS = `
    /* Standard image elements */
    img, picture, video, canvas,
    [role="img"] {
      opacity: 0 !important;
      pointer-events: none !important;
    }

    /* SVGs that are decorative images (not icons or UI elements) */
    svg:not([aria-hidden="true"]):not([width]):not([class*="icon"]) {
      opacity: 0 !important;
      pointer-events: none !important;
    }

    /* Lazy-load images that haven't loaded yet */
    img[data-src], img[data-lazy-src], img[data-original],
    img[loading="lazy"] {
      opacity: 0 !important;
      pointer-events: none !important;
    }
  `;

  /** Only target actual image URLs, not CSS gradients or layout backgrounds */
  function isImageUrl(bgValue: string): boolean {
    return /url\s*\(/.test(bgValue) && !/gradient\s*\(/.test(bgValue);
  }

  function hideBackgroundImages() {
    // Target elements likely to be image containers — not every element on the page.
    // Scanning all (*) kills section backgrounds, gradients, and overlays on Framer sites.
    const candidates = document.querySelectorAll<HTMLElement>(
      '[style*="background-image"], [style*="background:"], figure, ' +
      '[class*="image"], [class*="photo"], [class*="hero"], [class*="banner"], ' +
      '[data-framer-background-image-wrapper]'
    );
    for (const el of candidates) {
      if (el.closest('#accessify-root')) continue;
      const computed = getComputedStyle(el);
      if (computed.backgroundImage && computed.backgroundImage !== 'none' && isImageUrl(computed.backgroundImage)) {
        bgImageOriginals.set(el, el.style.backgroundImage);
        el.style.setProperty('background-image', 'none', 'important');
      }
    }
  }

  function restoreBackgroundImages() {
    for (const [el, original] of bgImageOriginals) {
      try {
        el.style.backgroundImage = original;
      } catch { /* element may be gone */ }
    }
    bgImageOriginals.clear();
  }

  function setupMutationObserver() {
    if (mutationObserver) return;
    mutationObserver = new MutationObserver((mutations) => {
      if (!enabled) return;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.closest('#accessify-root')) continue;
          // Hide background images only on actual image URLs (not gradients)
          const computed = getComputedStyle(node);
          if (computed.backgroundImage && computed.backgroundImage !== 'none' && isImageUrl(computed.backgroundImage)) {
            bgImageOriginals.set(node, node.style.backgroundImage);
            node.style.setProperty('background-image', 'none', 'important');
          }
          // Check image-related children (not all *)
          node.querySelectorAll<HTMLElement>('img, picture, video, [role="img"], [style*="background-image"]').forEach((child) => {
            if (child.closest('#accessify-root')) return;
            const cs = getComputedStyle(child);
            if (cs.backgroundImage && cs.backgroundImage !== 'none' && isImageUrl(cs.backgroundImage)) {
              bgImageOriginals.set(child, child.style.backgroundImage);
              child.style.setProperty('background-image', 'none', 'important');
            }
          });
        }
      }
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  function activate() {
    enabled = true;
    let styleEl = document.getElementById(STYLE_ID);
    if (!styleEl) {
      styleEl = document.createElement('style');
      styleEl.id = STYLE_ID;
      document.head.appendChild(styleEl);
    }
    styleEl.textContent = CSS;
    hideBackgroundImages();
    setupMutationObserver();
  }

  function deactivate() {
    enabled = false;
    mutationObserver?.disconnect();
    mutationObserver = null;
    restoreBackgroundImages();
    document.getElementById(STYLE_ID)?.remove();
  }

  return {
    id: 'hide-images',
    name: () => 'Hide Images',
    description: 'Remove images for distraction-free reading',
    icon: 'hide-images',
    category: 'visual',
    activate,
    deactivate,
    getState: (): FeatureState => ({ id: 'hide-images', enabled }),
  };
}
